import { FC, SyntheticEvent, useCallback } from "react";
import { Divider, Stack, Tab } from "@mui/material";
import { TabList } from "@mui/lab";
import { useFlag } from "@unleash/proxy-client-react";
import { metricAvailableResourceTypes } from "../../../utils/constants/metrics";
import { resourceDetailsTargetResourceTypeSelector } from "@/store/resource-details/selectors/target-resource/resourceDetailsTargetResourceTypeSelector";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setResourceDetailsActiveTab } from "@/store/resource-details/resourceDetailsSlice";
import { ResourceDetailsActiveTab } from "@/store/resource-details/utils/types/common";
import { resourceDetailsAvailableTabsSelector } from "@/store/resource-details/selectors/options/resourceDetailsAvailableTabsSelector";
import { ResourceDetailsHeaderK8SPodsTab } from "@/components/pages/common/resource-details/components/header/tabs/ResourceDetailsHeaderK8SPodsTab";
import { ResourceDetailsHeaderOpportunitiesTab } from "@/components/pages/common/resource-details/components/header/tabs/ResourceDetailsHeaderOpportunitiesTab";
import { ResourceDetailsHeaderCoverageTab } from "@/components/pages/common/resource-details/components/header/tabs/ResourceDetailsHeaderCoverageTab";
import { k8SResourceDetailsTargetPodsCountSelector } from "@/store/resource-details/selectors/target-resource/kubernetes/k8SResourceDetailsTargetPodsCountSelector";
import { resourceDetailsTargetResourceIdSelector } from "@/store/resource-details/selectors/target-resource/resourceDetailsTargetResourceIdSelector";
import { resourceDetailsResourceCoverageDataCoveragePercentSelector } from "@/store/resource-details/selectors/coverage-data/resourceDetailsResourceCoverageDataCoveragePercentSelector";
import { savingsOpportunitiesDataLengthSelector } from "@/store/savings-opportunities/selectors/savingsOpportunitiesDataLengthSelector";

export const ResourceDetailsHeaderTabs: FC = () => {
  const dispatch = useAppDispatch();
  const enableResourceDrawerCoverageDetails = useFlag(
    "EnableResourceDrawerCoverageDetails",
  );

  const availableTabs = useAppSelector(resourceDetailsAvailableTabsSelector);
  const resourceType = useAppSelector(
    resourceDetailsTargetResourceTypeSelector,
  );

  const k8sPodsCount = useAppSelector(
    k8SResourceDetailsTargetPodsCountSelector,
  );

  const resourceId = useAppSelector(resourceDetailsTargetResourceIdSelector);
  const coverage = useAppSelector(
    resourceDetailsResourceCoverageDataCoveragePercentSelector,
  );

  const opportunitiesCount = useAppSelector((state) =>
    savingsOpportunitiesDataLengthSelector(state, resourceId),
  );

  const handleTabChange = useCallback(
    (_: SyntheticEvent, tab: ResourceDetailsActiveTab) => {
      dispatch(setResourceDetailsActiveTab(tab));
    },
    [dispatch],
  );

  if (!availableTabs?.length) {
    return null;
  }

  return (
    <Stack>
      <TabList sx={{ bgcolor: "white" }} onChange={handleTabChange}>
        {availableTabs.includes("general") && (
          <Tab value="general" label="General" sx={{ textTransform: "none" }} />
        )}

        {availableTabs.includes("costs") && (
          <Tab value="costs" label="Costs" sx={{ textTransform: "none" }} />
        )}

        {availableTabs.includes("metrics") &&
          resourceType &&
          metricAvailableResourceTypes.has(resourceType) && (
            <Tab
              value="metrics"
              label="Metrics"
              sx={{ textTransform: "none" }}
            />
          )}

        {enableResourceDrawerCoverageDetails &&
          availableTabs.includes("coverage") &&
          ResourceDetailsHeaderCoverageTab(coverage)}

        {availableTabs.includes("k8sPods") &&
          ResourceDetailsHeaderK8SPodsTab(k8sPodsCount)}

        {availableTabs.includes("opportunities") &&
          ResourceDetailsHeaderOpportunitiesTab(opportunitiesCount)}
      </TabList>

      <Divider />
    </Stack>
  );
};
